import { PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import PropTypes from 'prop-types';

const chartThemes = {
  soft: ['#67c1eb', '#a9b1f5', '#f2aabb', '#ffa56e', '#a2c989', '#f79797', '#c09be0', '#82cfbd', '#dec562', '#7db0c9'],
  formal: ['#6388bf', '#9398db', '#d19f90', '#89ab9e', '#e3a3c2', '#8e94a3', '#b182cf', '#b4cfa3', '#c4a976', '#a6a29c'],  
  blue: ['#0D47A1', '#1565C0', '#1976D2', '#1E88E5', '#2196F3', '#42A5F5', '#64B5F6', '#90CAF9', '#BBDEFB', '#E3F2FD'],
  purple: ['#4C1D95', '#581C87', '#6B21A8', '#7E22CE', '#9333EA', '#A855F7', '#C084FC', '#D8B4FE', '#E9D5FF', '#F3E8FF'],
  magenta: ['#6D1B3B', '#880E4F', '#AD1457', '#C2185B', '#D81B60', '#EC4899', '#F06292', '#F48FB1', '#F8BBD0', '#FCE4EC'],
  teal: ['#004D40', '#00695C', '#00796B', '#00897B', '#009688', '#26A69A', '#4DB6AC', '#80CBC4', '#B2DFDB', '#E0F2F1'],
  red: ['#B71C1C', '#C62828', '#D32F2F', '#E53935', '#F44336', '#EF5350', '#E57373', '#EF9A9A', '#FFCDD2', '#FFEBEE'],
  orange: ['#BF360C', '#E65100', '#EF6C00', '#F57C00', '#FB8C00', '#FFA726', '#FFB300', '#FFCC80', '#FFE0B2', '#FFF3E0'],
  gray: ['#171923', '#1A202C', '#2D3748', '#4A5568', '#718096', '#A0AEC0', '#CBD5E0', '#E2E8F0', '#EDF2F7', '#F7FAFC'],
};

const PieChartComponent = ({ data, theme = 'soft', width = 400, height = 300 }) => {
  const colors = chartThemes[theme] || chartThemes.soft;

  return (
    <PieChart width={width} height={height}>
      <Pie
        data={data}
        cx="50%"
        cy="50%"
        outerRadius={100}
        fill="#8884d8"
        dataKey="value"
        label={({ name, value }) => `${name}: ${value.toLocaleString('th-TH')}`}
      >
        {data.map((entry, index) => (
          <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
        ))}
      </Pie>
      <Tooltip formatter={(value) => value.toLocaleString('th-TH')} />
      <Legend />
    </PieChart>
  );
};

export const PieChartPercentComponent = ({ 
  data, 
  theme = 'soft', 
  width = 400, 
  height = 300,
  innerRadius = 0,
  outerRadius = 100,
}) => {
  // Validate data prop
  if (!data || !Array.isArray(data) || data.length === 0) {
    return <div>No data available for pie chart</div>;
  }

  const colors = chartThemes[theme] || chartThemes.soft;

  // Calculate total for percentage calculation
  const total = data.reduce((sum, entry) => sum + entry.value, 0);

  return (
    <PieChart width={width} height={height}>
      <Pie
        data={data}
        cx="50%"
        cy="50%"
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        dataKey="value"
        label={({ name, value }) => {
          const percentage = ((value / total) * 100).toFixed(1);
          return `${name}: ${percentage}% (${value.toLocaleString('th-TH')})`;
        }}
        labelLine={true}
      >
        {data.map((entry, index) => (
          <Cell 
            key={`cell-${index}`} 
            fill={colors[index % colors.length]} 
          />
        ))}
      </Pie>
      <Tooltip 
        formatter={(value, name) => {
          const percentage = ((value / total) * 100).toFixed(1);
          return [`${percentage}% (${value.toLocaleString('th-TH')})`, name];
        }}
      />
      <Legend 
        layout="vertical" 
        align="right" 
        verticalAlign="middle"
      />
    </PieChart>
  );
};

PieChartPercentComponent.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    })
  ).isRequired,
  theme: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  innerRadius: PropTypes.number,
  outerRadius: PropTypes.number,
};

export default PieChartComponent;